import { useState, useEffect } from 'react';
import { History, ChevronDown, ChevronUp, TrendingDown, TrendingUp, Minus, Calendar, Fish, HelpCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import axios from 'axios';
import { useAuth } from '@/App';
import { classifyMfiTrend } from '@/utils/mfiTrend';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * RecordHistoryPanel — Shows revision history + MFI progression for a record.
 * Props:
 *   recordId    – the record's ID
 *   recordType  – 'spreadsheet' | 'growth'
 *   recordName  – display name
 *   onSaveMfi   – callback to trigger after MFI results are saved (optional)
 */
export default function RecordHistoryPanel({ recordId, recordType, recordName }) {
  const { token } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [mfiHistory, setMfiHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchHistory = async () => {
    if (!recordId || !token) return;
    setLoading(true);
    try {
      const [revRes, mfiRes] = await Promise.all([
        axios.get(`${API}/record-history/${recordType}/${recordId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/record-history/${recordType}/${recordId}/mfi-history`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setRevisions(revRes.data.revisions || []);
      setMfiHistory(mfiRes.data.mfi_history || []);
    } catch {
      // Silently fail — history is informational
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded && revisions.length === 0 && mfiHistory.length === 0) {
      fetchHistory();
    }
  }, [expanded, recordId]);

  const hasHistory = revisions.length > 0 || mfiHistory.length > 0;

  const formatDate = (isoStr) => {
    if (!isoStr) return '—';
    try {
      return new Date(isoStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return isoStr;
    }
  };

  if (!recordId) return null;

  return (
    <Card className="border-slate-200 mt-4" data-testid="record-history-panel">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-50 transition-colors rounded-t-lg"
        data-testid="record-history-toggle"
      >
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">
            Record History
            {hasHistory && (
              <span className="ml-1.5 text-xs text-slate-400">
                ({revisions.length} revision{revisions.length !== 1 ? 's' : ''}{mfiHistory.length > 0 ? `, ${mfiHistory.length} MFI run${mfiHistory.length !== 1 ? 's' : ''}` : ''})
              </span>
            )}
          </span>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {expanded && (
        <CardContent className="pt-0 pb-4 space-y-4">
          {loading && <p className="text-xs text-slate-400 py-2">Loading history...</p>}

          {!loading && !hasHistory && (
            <p className="text-xs text-slate-400 py-2">No revision history yet. History is created automatically when you update or re-upload data.</p>
          )}

          {/* MFI Progression Timeline */}
          {mfiHistory.length > 0 && (
            <div data-testid="mfi-history-section">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Fish className="h-3.5 w-3.5" />
                Missing Fish Estimate Progression
              </p>

              {/* ★ Trend classifier — confidence-weighted so MONITOR_ONLY runs don't swing the label */}
              {(() => {
                const t = classifyMfiTrend(mfiHistory);
                const trendStyles = {
                  Increasing: { bg: 'bg-red-50 border-red-200', dot: 'bg-red-500', label: 'text-red-700', icon: <TrendingUp className="h-3.5 w-3.5" /> },
                  Decreasing: { bg: 'bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500', label: 'text-emerald-700', icon: <TrendingDown className="h-3.5 w-3.5" /> },
                  Stable:     { bg: 'bg-sky-50 border-sky-200', dot: 'bg-sky-500', label: 'text-sky-700', icon: <Minus className="h-3.5 w-3.5" /> },
                  Uncertain:  { bg: 'bg-slate-50 border-slate-200', dot: 'bg-slate-400', label: 'text-slate-600', icon: <HelpCircle className="h-3.5 w-3.5" /> },
                };
                const s = trendStyles[t.trend] || trendStyles.Uncertain;
                return (
                  <div className={`rounded-md border p-2.5 mb-2 ${s.bg}`} data-testid="mfi-trend-callout">
                    <div className="flex items-center justify-between gap-3">
                      <div className={`flex items-center gap-2 ${s.label}`}>
                        <span className={`inline-block w-2 h-2 rounded-full ${s.dot}`} />
                        {s.icon}
                        <span className="text-xs font-semibold" data-testid="mfi-trend-label">Trend: {t.trend}</span>
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-white/70 text-slate-600" data-testid="mfi-trend-confidence">
                          {t.confidence} confidence
                        </span>
                      </div>
                      <span className="text-[10px] text-slate-500" data-testid="mfi-trend-usable">
                        {t.usableCount}/{t.totalCount} usable run{t.totalCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-600 mt-1 leading-relaxed" data-testid="mfi-trend-reason">{t.reason}</p>
                    <p className="text-[10px] text-slate-400 mt-1 italic">
                      Low-confidence runs (MONITOR_ONLY / Indication Only) are excluded from the trend calculation so they cannot create fake swings.
                    </p>
                  </div>
                );
              })()}

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-1.5 px-2 text-slate-500 font-medium">Date</th>
                      <th className="text-right py-1.5 px-2 text-slate-500 font-medium">Est. Missing</th>
                      <th className="text-right py-1.5 px-2 text-slate-500 font-medium">Deviation</th>
                      <th className="text-left py-1.5 px-2 text-slate-500 font-medium">Strength</th>
                      <th className="text-left py-1.5 px-2 text-slate-500 font-medium">Interpretation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mfiHistory.map((mfi, i) => {
                      const prev = i > 0 ? mfiHistory[i - 1] : null;
                      const delta = prev ? mfi.estimated_missing - prev.estimated_missing : 0;
                      return (
                        <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-1.5 px-2 text-slate-600 whitespace-nowrap">{formatDate(mfi.date)}</td>
                          <td className="py-1.5 px-2 text-right font-medium">
                            <span className={mfi.has_missing_fish ? 'text-amber-700' : 'text-green-600'}>
                              {mfi.estimated_missing > 0 ? `~${mfi.estimated_missing.toLocaleString()}` : '0'}
                            </span>
                            {delta !== 0 && (
                              <span className={`ml-1 text-[10px] ${delta > 0 ? 'text-red-500' : 'text-green-500'}`}>
                                {delta > 0 ? '+' : ''}{delta.toLocaleString()}
                              </span>
                            )}
                          </td>
                          <td className="py-1.5 px-2 text-right text-slate-600">
                            {mfi.deviation_percent != null ? `${mfi.deviation_percent}%` : '—'}
                          </td>
                          <td className="py-1.5 px-2">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              mfi.detection_strength === 'High' ? 'bg-red-100 text-red-700' :
                              mfi.detection_strength === 'Moderate' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {mfi.detection_strength || 'Low'}
                            </span>
                          </td>
                          <td className="py-1.5 px-2 text-slate-500 max-w-[200px] truncate">
                            {mfi.interpretation || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mini trend chart */}
              {mfiHistory.length >= 2 && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg" data-testid="mfi-trend-chart">
                  <p className="text-[10px] text-slate-500 mb-2 font-medium">Missing Fish Trend</p>
                  <div className="flex items-end gap-1 h-16">
                    {mfiHistory.map((mfi, i) => {
                      const maxVal = Math.max(...mfiHistory.map(m => m.estimated_missing || 0), 1);
                      const height = maxVal > 0 ? Math.max(4, (mfi.estimated_missing / maxVal) * 64) : 4;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${formatDate(mfi.date)}: ~${(mfi.estimated_missing || 0).toLocaleString()}`}>
                          <div
                            className={`w-full rounded-t ${mfi.has_missing_fish ? 'bg-amber-400' : 'bg-green-400'}`}
                            style={{ height: `${height}px` }}
                          />
                          <span className="text-[8px] text-slate-400 truncate w-full text-center">{formatDate(mfi.date).split(' ').slice(0, 2).join(' ')}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Revision History Table */}
          {revisions.length > 0 && (
            <div data-testid="revision-history-section">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                Data Upload Revisions
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-1.5 px-2 text-slate-500 font-medium">#</th>
                      <th className="text-left py-1.5 px-2 text-slate-500 font-medium">Date</th>
                      <th className="text-right py-1.5 px-2 text-slate-500 font-medium">Samples</th>
                      <th className="text-right py-1.5 px-2 text-slate-500 font-medium">Days</th>
                      <th className="text-right py-1.5 px-2 text-slate-500 font-medium">Last Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revisions.map((rev, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-1.5 px-2 text-slate-400">v{rev.revision_number}</td>
                        <td className="py-1.5 px-2 text-slate-600 whitespace-nowrap">{formatDate(rev.revision_date)}</td>
                        <td className="py-1.5 px-2 text-right text-slate-600">{rev.key_inputs?.sample_count || '—'}</td>
                        <td className="py-1.5 px-2 text-right text-slate-600">{rev.key_inputs?.days_in_water || rev.key_inputs?.data_points_count || '—'}</td>
                        <td className="py-1.5 px-2 text-right text-slate-600">
                          {rev.key_inputs?.last_sample_weight ? `${rev.key_inputs.last_sample_weight}g` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

/**
 * Helper: Save MFI results to record history from the frontend
 */
export async function saveMfiToHistory(token, recordType, recordId, mfiData) {
  try {
    await axios.post(`${API}/record-history/${recordType}/${recordId}/mfi-result`, mfiData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch {
    // Silently fail — history saving is non-blocking
  }
}
